import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';


import { AccountService } from '../../../../../../services/account/account.service';
import { EventTypeService } from '../../../../../../services/event-type/event-type.service';

import { EventType } from '../../../../../../models/event-type';
import { CapitanLocation, LocationArea } from '../../../../../../models/location';
import { LocationService } from '../../../../../../services/location/location.service';
import { StandardColorTeal } from '../../../../../../utils';
import { TaxRateService } from '../../../../../../services/tax-rate/tax-rate.service';
import { TaxRate } from '../../../../../../models/tax-rate';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { CurrencyRefundAmount, EventTypeStatus, EventTypeWaitlistScope, ScheduleType } from '../../../../../../models/event-type-enums';
import { ComponentHasUnsavedChanges } from '../../../../../../unsaved-changes-guard.service';
import {
  AddEventTypeParticipantAgreementComponent
} from './modals/add-event-type-participant-agreement/add-event-type-participant-agreement.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  AddEventTypeRequiredWaiverComponent
} from './modals/add-event-type-required-waiver/add-event-type-required-waiver.component';

@Component({
  selector: 'app-event-type-edit',
  templateUrl: './event-type-edit.component.html',
  styleUrls: ['./event-type-edit.component.css']
})
export class EventTypeEditComponent implements OnInit, ComponentHasUnsavedChanges {

  readonly SUBMIT_TYPE_GO_BACK = 'goBack';
  readonly SUBMIT_TYPE_CONTINUE = 'continue';

  public isNewEventType: boolean = null;
  public eventType: EventType;
  public hasRequiredWaivers = false;
  public hasParticipantAgreements = false;
  public submitType = this.SUBMIT_TYPE_CONTINUE;

  public locationList: CapitanLocation[] = null;
  public locationAreaList: LocationArea[];
  public taxRateList: TaxRate[];

  public eventTypeErrorMessage = '';
  public eventTypeErrors: any = {};

  public loadingEventType = false;
  public loadingLocationAreas = true;
  public loadingTaxRates = true;

  public eventTypeNotFound = false;
  public serverError: number = null;
  public unsavedChanges = false;

  // Create instance references to enums to they may be called in the template.
  public readonly ScheduleType = ScheduleType;
  public readonly EventTypeStatus = EventTypeStatus;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public orgConfig: OrganizationConfigService,
    private accountService: AccountService,
    private eventTypeService: EventTypeService,
    private locationService: LocationService,
    private taxRateService: TaxRateService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    this.isNewEventType = !routeParams.hasOwnProperty('id');

    // Either get the event type to edit (if an ID was provided) or construct an
    // empty event type for creation.
    this.resetEventType();
    if (!this.isNewEventType) {
      this.loadEventType(routeParams.id);
    }

    this.loadLocations();
    this.loadLocationAreas();
    this.loadTaxRates();
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  /**
   * Set the entered event type information back to the default values.
   */
  public resetEventType() {
    this.eventType = {
      id: null,
      url: '',
      schedule_type: null,
      name: '',
      description_text: '',
      status: EventTypeStatus.draft,
      reserves_area_capacity: false,
      late_booking_window_minutes: 15,
      is_listed: true,
      has_automatic_check_in: false,
      allow_customer_cancellation: false,
      customer_cancellation_window_hours: 0,
      customer_cancellation_currency_refund_amount: CurrencyRefundAmount.full,
      customer_cancellation_currency_refund_as_credit: false,
      refund_voucher_on_customer_cancellation: true,
      calendar_color_hex: StandardColorTeal.colorHex,
      require_login_for_booking: false,
      tax_rate_id: null,
      tax_rate_name: null,
      max_participant_count: 6,
      waitlist_scope: EventTypeWaitlistScope.event,
      allow_customers_to_join_waitlist: false,
      display_waitlist_count_in_climber_app: false,
      display_pricing_in_climber_app_listings: false,
      display_remaining_capacity_in_climber_app_listings: null,
      automatically_invite_off_waitlist: true,
      min_hours_before_event_for_automatic_waitlist_invite: 0,
      hours_to_accept_waitlist_spot: null,
      allow_waitlist_customers_to_select_preferred_days_of_week: false,
      allow_waitlist_customers_to_select_preferred_locations: false,
      require_payment_method_to_join_waitlist: false,
      require_membership_ts_and_cs_to_join_waitlist: false,
      mailchimp_check_in_participant_event_name: '',
      mailchimp_check_in_booking_customer_event_name: '',
      mailchimp_completed_participant_event_name: '',
      mailchimp_completed_booking_customer_event_name: '',
      scheduled_events_columns: null,
      waitlist_staff_notification_email_addresses: '',
      waitlist_confirmation_email_text: '',
      is_private_event_request_form: false,
      participant_agreement_documents: [],
      required_waiver_documents: [],
      pricing_membership_id: null,
      has_limited_location_availability: false,
      allowed_location_ids: [],
      allowed_location_names: [],
      created_at: null,
      updated_at: null,
      questions_url: '',
      schedules_url: '',
      capitan_scheduler_url: ''
    };
  }

  /**
   * Load the event type with the given ID (only used for editing event types).
   *
   * @param eventTypeId - The API ID of the event type.
   */
  public loadEventType(eventTypeId: string) {
    this.loadingEventType = true;

    const eventTypeIdInt = parseInt(eventTypeId, 10);
    if (isNaN(eventTypeIdInt)) {
      this.eventTypeNotFound = true;
      this.loadingEventType = false;
      return;
    }

    this.eventTypeService
      .getEventType(eventTypeIdInt)
      .then(eventType => {
        this.eventType = eventType;
        if (this.eventType.required_waiver_documents.length > 0) {
          this.hasRequiredWaivers = true;
        }
        if (this.eventType.participant_agreement_documents.length > 0) {
          this.hasParticipantAgreements = true;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.eventTypeNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventType = false; });
  }

  public loadTaxRates() {
    this.loadingTaxRates = true;

    this.taxRateService
      .getTaxRates(1, 100)
      .then(response => {
        this.taxRateList = response.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingTaxRates = false; });
  }

  /**
   * Handle an event type form submission.
   */
  public onEventTypeSubmit() {
    this.loadingEventType = true;

    this.eventTypeErrorMessage = '';
    this.eventTypeErrors = {};

    if (!this.hasRequiredWaivers) {
      this.eventType.required_waiver_documents = [];
    }
    if (!this.hasParticipantAgreements) {
      this.eventType.participant_agreement_documents = [];
    }

    let successMessage = '';

    // Success handling is different between creating and updating.
    let eventTypeSubmitPromise: Promise<EventType>;
    if (this.isNewEventType) {

      eventTypeSubmitPromise = this.eventTypeService
        .addEventType(this.eventType);

      successMessage = 'Event \'' + this.eventType.name + '\' added successfully.';

    } else {

      eventTypeSubmitPromise = this.eventTypeService
        .updateEventTypeDetails(this.eventType);

      successMessage = 'Event \'' + this.eventType.name + '\' saved successfully.';

    }

    // Error handling is the same for both creating and updating.
    eventTypeSubmitPromise
      .then(savedEventType => {
        this.unsavedChanges = false;

        this.eventType = savedEventType;

        // Routing depends on which save button was clicked.
        if (this.submitType === this.SUBMIT_TYPE_GO_BACK) {
          this.router.navigate(
            [
              '/events/setup/event-types'
            ],
            {
              queryParams: {
                status: this.eventType.status
              },
              state: {
                feedback: {
                  text: successMessage,
                  status: 'success'
                }
              }
            }
          );
        } else if (this.submitType === this.SUBMIT_TYPE_CONTINUE) {
          if (savedEventType.schedule_type === ScheduleType.rolling) {
            this.router.navigate(
              ['/events/setup/edit/' + this.eventType.id.toString() + '/pricing-membership']
            );
          } else {
            this.router.navigate(
              ['/events/setup/edit/' + this.eventType.id.toString() + '/pricing-matrices']
            );
          }
        }
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.eventTypeErrors = reason.error;
          this.eventTypeErrorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          if (this.isNewEventType) {
            this.eventTypeErrorMessage = 'You do not have permission to add events.';
          } else {
            this.eventTypeErrorMessage = 'You do not have permission to modify this event.';
          }
          this.unsavedChanges = false;
        } else {
          this.serverError = reason.status;
          this.unsavedChanges = false;
        }
      })
      .finally(() => { this.loadingEventType = false; });
  }

  public taxRateRemoved(taxRateId: number | null) {
    if (taxRateId === null) { return false; }

    if (!this.taxRateList) { return true; }

    for (const taxRate of this.taxRateList) {
      if (taxRate.id === taxRateId) { return false; }
    }

    return true;
  }

  public openAddRequiredWaiverModal() {
    const modalRef = this.modalService.open(AddEventTypeRequiredWaiverComponent);
    modalRef.componentInstance.eventType = this.eventType;
    modalRef.componentInstance.submittedCallback = () => {
      this.unsavedChanges = true;
    };
  }

  public openAddParticipantAgreementModal() {
    const modalRef = this.modalService.open(AddEventTypeParticipantAgreementComponent);
    modalRef.componentInstance.eventType = this.eventType;
    modalRef.componentInstance.submittedCallback = () => {
      this.unsavedChanges = true;
    };
  }

  private loadLocations() {
    this.locationList = this.locationService.getLocations();

    if (this.locationList === null) {
      this.locationService
        .loadLocations()
        .then(() => {
          this.locationList = this.locationService.getLocations();
        });
    }
  }

  private loadLocationAreas() {
    this.loadingLocationAreas = true;

    this.locationService
      .getLocationAreas(
        1,
        100,
        {gym_id: this.accountService.getMyAccount().default_gym_id}
        )
      .then(response => { this.locationAreaList = response.results; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingLocationAreas = false; });
  }
}
